import {
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import {
  AgentChatAttachment,
  AgentChatMessage,
  MessageType,
  TAgentAIMessageChunk,
} from '../types/agent-chat-types';

export const agentChatMessageAdapter = createEntityAdapter<
  AgentChatMessage,
  string
>({
  selectId: (message: AgentChatMessage) => message.message_id,
  sortComparer: (a, b) => a.order - b.order,
});
const agentChatMessageSelectors = agentChatMessageAdapter.getSelectors();

export const selectMessagesByThread = createSelector(
  [agentChatMessageSelectors.selectAll, (state, threadId: string) => threadId],
  (messages, threadId) => {
    return messages.filter((message) => message.thread_id === threadId);
  },
);

const selectLastMessageOrder = createSelector(
  [selectMessagesByThread],
  (messages) => {
    if (messages.length === 0) {
      return 0;
    }

    return Math.max(...messages.map((message) => message.order));
  },
);

interface NewMessagePayload {
  threadId: string;
  messageId: string;
  message: MessageType;
  attachments: AgentChatAttachment[];
}

interface UpdateMessagePayload {
  threadId: string;
  messageId: string;
  message: MessageType;
}

interface HandleAgentMessageChunkPayload {
  threadId: string;
  message: TAgentAIMessageChunk;
}

export const agentChatSlice = createSlice({
  name: 'agentChat',
  initialState: agentChatMessageAdapter.getInitialState(),
  reducers: {
    messageListed: (state, action: PayloadAction<AgentChatMessage[]>) => {
      return agentChatMessageAdapter.upsertMany(state, action.payload);
    },
    messageListReplaced: (
      state,
      action: PayloadAction<{ threadId: string; messages: AgentChatMessage[] }>,
    ) => {
      // Remove all existing messages for this thread, then set the server's truth
      const existingIds = agentChatMessageSelectors
        .selectAll(state)
        .filter((m) => m.thread_id === action.payload.threadId)
        .map((m) => m.message_id);
      agentChatMessageAdapter.removeMany(state, existingIds);
      agentChatMessageAdapter.upsertMany(state, action.payload.messages);
    },
    messageAdded: (state, action: PayloadAction<NewMessagePayload>) => {
      const last_index = selectLastMessageOrder(state, action.payload.threadId);

      return agentChatMessageAdapter.addOne(state, {
        message_id: action.payload.messageId,
        message: action.payload.message,
        thread_id: action.payload.threadId,
        order: last_index + 1,
        created_at: new Date().toISOString(),
        attachments: action.payload.attachments,
      });
    },
    messageUpdated: (state, action: PayloadAction<UpdateMessagePayload>) => {
      const last_index = selectLastMessageOrder(state, action.payload.threadId);

      return agentChatMessageAdapter.upsertOne(state, {
        message_id: action.payload.messageId,
        message: action.payload.message,
        thread_id: action.payload.threadId,
        order: last_index + 1,
        created_at: new Date().toISOString(),
        attachments: [],
      });
    },
    messageDeleted: (state, action: PayloadAction<string>) => {
      agentChatMessageAdapter.removeOne(state, action.payload);
    },
    handleAgentMessageChunk: (
      state,
      action: PayloadAction<HandleAgentMessageChunkPayload>,
    ) => {
      const { threadId, message } = action.payload;

      if (message.id === null) {
        return;
      }

      const existingMessage = agentChatMessageSelectors.selectById(
        state,
        message.id,
      );

      const last_index = selectLastMessageOrder(state, threadId);

      // Helper to extract text from content (handles both string and array formats)
      const extractText = (
        content: string | (string | Record<string, unknown>)[],
      ): string => {
        if (typeof content === 'string') {
          return content;
        }
        // Handle array content (e.g., from Gemini: [{type: 'text', text: '...'}])
        return content
          .map((item) => {
            if (typeof item === 'string') return item;
            if (typeof item === 'object' && item !== null && 'text' in item) {
              return String(item.text);
            }
            return '';
          })
          .join('');
      };

      if (!existingMessage) {
        // Store new message with normalized string content for easier concatenation
        const initialText = extractText(message.content);
        agentChatMessageAdapter.addOne(state, {
          message_id: message.id,
          message: {
            ...message,
            content: initialText,
          },
          thread_id: threadId,
          order: last_index + 1,
          created_at: new Date().toISOString(),
          attachments: [],
        });
        return;
      }

      // Append new content to existing message
      const existingText = extractText(existingMessage.message.content);
      const newText = extractText(message.content);

      agentChatMessageAdapter.updateOne(state, {
        id: message.id,
        changes: {
          message: {
            ...existingMessage.message,
            content: existingText + newText,
          },
        },
      });
    },
  },
});
